import DOMPurify from 'dompurify'

/**
 * 安全的 HTML 內容清理工具
 * 用於防止 XSS 攻擊，同時保留安全的 HTML 格式
 */

// 允許的 HTML 標籤（針對富文本編輯器常用標籤）
const ALLOWED_TAGS = [
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6',
	'p',
	'br',
	'div',
	'span',
	'strong',
	'b',
	'em',
	'i',
	'u',
	'ul',
	'ol',
	'li',
	'a',
	'img',
	'table',
	'thead',
	'tbody',
	'tr',
	'td',
	'th',
	'blockquote',
	'pre',
	'code',
]

// 允許的 HTML 屬性
const ALLOWED_ATTR = [
	'style',
	'class',
	'id',
	'href',
	'target',
	'rel',
	'src',
	'alt',
	'width',
	'height',
	'colspan',
	'rowspan',
]

// 允許的 CSS 屬性
const ALLOWED_CSS = [
	'color',
	'background-color',
	'font-size',
	'font-weight',
	'font-style',
	'text-align',
	'text-decoration',
	'margin',
	'padding',
	'border',
	'border-color',
	'border-width',
	'border-style',
]

/**
 * 清理 HTML 內容，防止 XSS 攻擊
 * @param {string} content - 需要清理的 HTML 內容
 * @param {object} options - 自定義配置選項
 * @returns {string} 清理後的安全 HTML 內容
 */
export const sanitizeHtml = (content, options = {}) => {
	if (!content || typeof content !== 'string') {
		return ''
	}

	// 合併默認配置和自定義配置
	const config = {
		ALLOWED_TAGS: options.allowedTags || ALLOWED_TAGS,
		ALLOWED_ATTR: options.allowedAttr || ALLOWED_ATTR,
		ALLOWED_CSS: options.allowedCss || ALLOWED_CSS,
		// 禁止危險的標籤和屬性
		FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'iframe'],
		FORBID_ATTR: [
			'onerror',
			'onload',
			'onclick',
			'onmouseover',
			'onfocus',
			'onblur',
		],
		// 移除空白標籤
		REMOVE_EMPTY_TAGS: true,
		// 轉換相對 URL 為絕對 URL（如果需要）
		WHOLE_DOCUMENT: false,
		// 返回 DOM 節點而不是字符串（設為 false 返回字符串）
		RETURN_DOM: false,
		// 返回 DOM 片段
		RETURN_DOM_FRAGMENT: false,
	}

	try {
		// 使用 DOMPurify 清理內容
		const cleanContent = DOMPurify.sanitize(content, config)

		// 記錄清理結果（僅在開發環境）
		if (process.env.NODE_ENV === 'development' && content !== cleanContent) {
			console.warn('HTML 內容已被清理:', {
				original: content,
				cleaned: cleanContent,
			})
		}

		return cleanContent
	} catch (error) {
		console.error('HTML 清理失敗:', error)
		// 如果清理失敗，返回空字符串以確保安全
		return ''
	}
}

/**
 * 嚴格的 HTML 清理（只保留最基本的格式）
 * @param {string} content - 需要清理的 HTML 內容
 * @returns {string} 清理後的安全 HTML 內容
 */
export const sanitizeHtmlStrict = content => {
	return sanitizeHtml(content, {
		allowedTags: ['p', 'br', 'strong', 'b', 'em', 'i', 'u'],
		allowedAttr: ['style'],
		allowedCss: ['color', 'font-weight', 'font-style', 'text-decoration'],
	})
}

/**
 * 清理純文本內容（移除所有 HTML 標籤）
 * @param {string} content - 需要清理的內容
 * @returns {string} 純文本內容
 */
export const sanitizeText = content => {
	if (!content || typeof content !== 'string') {
		return ''
	}

	try {
		// 使用 DOMPurify 移除所有 HTML 標籤
		return DOMPurify.sanitize(content, {
			ALLOWED_TAGS: [],
			ALLOWED_ATTR: [],
			KEEP_CONTENT: true,
		})
	} catch (error) {
		console.error('文本清理失敗:', error)
		return content.replace(/<[^>]*>/g, '') // 回退方案：使用正則表達式移除標籤
	}
}

/**
 * 驗證 HTML 內容是否安全
 * @param {string} content - 需要驗證的 HTML 內容
 * @returns {boolean} 是否安全
 */
export const isHtmlSafe = content => {
	if (!content || typeof content !== 'string') {
		return true
	}

	const cleaned = sanitizeHtml(content)
	return content === cleaned
}

/**
 * 獲取 HTML 內容的純文本版本
 * @param {string} content - HTML 內容
 * @returns {string} 純文本內容
 */
export const getTextContent = content => {
	if (!content || typeof content !== 'string') {
		return ''
	}

	try {
		// 創建一個臨時的 DOM 元素來提取文本
		const tempDiv = document.createElement('div')
		tempDiv.innerHTML = sanitizeHtml(content)
		return tempDiv.textContent || tempDiv.innerText || ''
	} catch (error) {
		console.error('提取文本內容失敗:', error)
		return sanitizeText(content)
	}
}

export default {
	sanitizeHtml,
	sanitizeHtmlStrict,
	sanitizeText,
	isHtmlSafe,
	getTextContent,
}
